import Request from "../Request";
import { ClientLevelItem, LocationLevelItem, CategoryLevelItem, CategoryLocationLevelItem } from "../../interfaces/dashboards/PerformanceDashboard.interface";

class PerformanceDashboardService {
  static async getSimpleDashboardDataForClient({ clientId, epochMonth = null, compareEpochMonth = null }: { clientId: string|number, epochMonth: number | null, compareEpochMonth: number | null }) {
    try {
        const params = epochMonth && compareEpochMonth ? { targetEpochMonth: epochMonth, compareEpochMonth } : {};
        const response = await Request.get(`/api/clients/${clientId}/simple-dashboard-data`, params);
        return response
    } catch (error) {
        console.error(error);
    }
  }

  static moneyFormat(value: number, wholeNumber: boolean = true): string {
    return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD', 
        minimumFractionDigits: wholeNumber ? 0 : 2, 
        maximumFractionDigits: wholeNumber ? 0 : 2 
    }).format(value);
  }

  static percentageFormat(value: number, wholeNumber: boolean = true): string {
    return new Intl.NumberFormat('en-US', { 
        style: 'percent', 
        minimumFractionDigits: wholeNumber ? 0 : 2, 
        maximumFractionDigits: wholeNumber ? 0 : 2 
    }).format(value);
  }

  static calculateFreshness(inflow: number, inventory: number) {
    return inventory ? (100 * inflow / inventory) : 0;
  }

  static calculateWeeksOnHand(sales: number, inventory: number) {
    if (!sales || !inventory) {
      return `0 weeks`;
    }
    return `${(52 / (sales / inventory)).toFixed(0)} weeks`;
  }

  static calculateMissedSales(inventoryPlan: number, inventory: number, sales: number) {
    if (!inventoryPlan || !inventory || !sales) {
      return 0
    }
    const understocked = inventory - inventoryPlan;
    if (understocked > 0) {
      return 0
    }
    return understocked / (inventory / sales);
  }
  
  static calculateEfficiency({ salesSumLastMonth, inventory, inflow }: { salesSumLastMonth: number, inventory: number, inflow: number }): number {
    const inventoryPlusInflow = inventory + inflow;
    if (inventoryPlusInflow === 0) {
      return 0;
    }
    return salesSumLastMonth / inventoryPlusInflow;
  }

  /**
   * Converts location-level data to client-level data - either filtered or aggregated
   * @param locationLevelData Array of location data items
   * @param locationCode Optional location code to filter by
   * @returns Client-level data array
   */
  static convertLocationLevelToClientLevel(
    locationLevelData: LocationLevelItem[],
    locationCode?: string
  ): ClientLevelItem[] {
    // Filter data if locationCode provided
    const filteredData = locationCode
      ? locationLevelData.filter(item => item.locationCode === locationCode)
      : locationLevelData;

    // Group by epochMonthRm using Map
    const grouped = filteredData.reduce((map, item) => {
      const month = item.epochMonthRm;
      const existing = map.get(month) ?? {
        trend: 0,
        epochMonthSpan: month,
        inventoryBOMPlan: 0,
        inventoryBOMRM: 0,
        inventoryDeviationRm: 0,
        otbRetailEom: 0,
        salesPlan: 0,
        salesRm: 0,
        markdownsRm: 0,
        markdownsPlan: 0,
        inflowRetailRm: 0
      };

      // Sum all metrics in one operation
      const metrics = [
        'inventoryBOMPlan', 'inventoryBOMRM', 'inventoryDeviationRm',
        'otbRetailEom', 'salesPlan', 'salesRm', 
        'markdownsRm', 'markdownsPlan', 'inflowRetailRm', 'inflowPlanRetailRm'
      ] as const;

      metrics.forEach(metric => {
        existing[metric] += item[metric] || 0;
      });

      // Get max of following
      const maxMetrics = [
        'trend',
      ] as const;

      maxMetrics.forEach(metric => {
        existing[metric] = Math.max(existing[metric], item[metric] || 0);
      });

      return map.set(month, existing);
    }, new Map<number, ClientLevelItem>());

    return Array.from(grouped.values());
  }

  /**
   * Converts category-location level data to category level data
   * @param categoryLocationLevelData Array of category-location data items
   * @param locationCode Optional location code to filter by
   * @returns Category-level data array
   */
  static convertCategoryLocationLevelToCategoryLevel(
    categoryLocationLevelData: CategoryLocationLevelItem[],
    locationCode?: string
  ): CategoryLevelItem[] {
    // Filter data if locationCode provided
    const filteredData = locationCode
      ? categoryLocationLevelData.filter(item => item.locationCode === locationCode)
      : categoryLocationLevelData;

    // Group by epochMonthRm using Map
    const grouped = filteredData.reduce((map, item) => {
      const month = item.epochMonthRM;
      const existing = map.get(month) ?? {
        category: item.category,
        className: item.className,
        epochMonthRM: month,
        inventoryBomRm: 0,
        inventoryBomPlan: 0,
        inflowRetailRm: 0,
        salesSumLm: 0,
        inventoryBomAvgLm: 0,
        otbRetailEom: 0,
        salesRm: 0
      };

      // Sum all metrics in one operation
      const metrics = [
        'inventoryBomRm', 'inventoryBomPlan', 'inflowRetailRm',
        'salesSumLm', 'inventoryBomAvgLm', 'otbRetailEom', 'salesRm'
      ] as const;

      metrics.forEach(metric => {
        existing[metric] += item[metric] || 0;
      });

      return map.set(month, existing);
    }, new Map<number, CategoryLevelItem>());

    return Array.from(grouped.values());
  }
}

export default PerformanceDashboardService;